import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { dataset, type Dataset } from "../data/generator";
import { loadSeedDataset } from "../data/loadSeed";
import { DEFAULT_PRICE_PER_FUE, DEFAULT_RATIO, type RatioTable } from "../domain/rules";
import { basket, totals, type Kpis } from "../domain/aggregate";
import { useContract } from "./contract";
import { resolveIdentity } from "../domain/identity";
import type { IdentityKey, IdentityResolution } from "../domain/types";

interface AppState {
  ds: Dataset;
  ratio: RatioTable;
  setRatio: (c: keyof RatioTable, v: number) => void;
  resetRatio: () => void;
  /** Setzt Verhältnistabelle, Preis, Schlüssel und Warenkorb auf den Auslieferungszustand — für eine reproduzierbare Demo. */
  resetAll: () => void;
  ratioIsDefault: boolean;
  price: number;
  setPrice: (p: number) => void;
  identityKey: IdentityKey;
  setIdentityKey: (k: IdentityKey) => void;
  basketIds: string[];
  toggleLever: (id: string) => void;
  clearBasket: () => void;
  inBasket: (id: string) => boolean;
  kpis: Kpis;
  identity: IdentityResolution;
  /** Konsolidierter Bedarf (Personalnummer) — die „saubere" Referenz. */
  consolidatedFue: number;
  /** Vertragszahl aus der Vertragsgrundlage je Klasse (Einstellungen / Vertrag). */
  contracted: number;
  /** Woher die Daten kommen: Seed-Datei oder Demo-Generator. */
  dataSource: DataSource;
  basketResult: ReturnType<typeof basket>;
}

const Ctx = createContext<AppState | null>(null);

export type DataSource = "seed" | "generator";

/**
 * Datenquelle: Standard ist das Seed (public/seed/dataset.json — echte Exportdateien,
 * in der App klassifiziert). `?source=generator` schaltet auf die Demo-Kohorten;
 * Tests laufen immer synchron auf dem Generator.
 */
function chosenSource(): DataSource {
  if (import.meta.env.MODE === "test") return "generator";
  try { const q = new URLSearchParams(window.location.search).get("source"); if (q === "generator" || q === "seed") return q; } catch { /* kein window */ }
  return "seed";
}

export function AppProvider({ children, initialIdentityKey = "personalnummer", source }: { children: ReactNode; initialIdentityKey?: IdentityKey; source?: DataSource }) {
  const src = source ?? chosenSource();
  const [loaded, setLoaded] = useState<Dataset | null>(() => (src === "generator" ? dataset() : null));
  const [loadError, setLoadError] = useState<string | null>(null);
  useEffect(() => {
    if (src !== "seed" || loaded) return;
    let alive = true;
    loadSeedDataset().then((d) => { if (alive) setLoaded(d); }).catch((e) => { if (alive) { setLoadError(String(e)); setLoaded(dataset()); } });
    return () => { alive = false; };
  }, [src, loaded]);
  if (!loaded) return <LadeHinweis />;
  return <AppProviderInner ds={loaded} loadError={loadError} initialIdentityKey={initialIdentityKey}>{children}</AppProviderInner>;
}

function LadeHinweis() {
  return (
    <div className="flex min-h-screen items-center justify-center p-8" style={{ backgroundColor: "var(--surface-bg)", color: "var(--text-dim)" }}>
      <div className="text-center">
        <div className="text-strong text-lg font-semibold">Daten werden geladen …</div>
        <div className="mt-1 text-sm">60.000 Personen, 133.000 Konten — Klassifikation läuft im Browser gegen das Regelwerk V1.70.</div>
      </div>
    </div>
  );
}

function AppProviderInner({ children, ds, loadError, initialIdentityKey }: { children: ReactNode; ds: Dataset; loadError: string | null; initialIdentityKey: IdentityKey }) {
  void loadError;
  const [ratio, setRatioState] = useState<RatioTable>({ ...DEFAULT_RATIO });
  const [price, setPrice] = useState(DEFAULT_PRICE_PER_FUE);
  const [identityKey, setIdentityKey] = useState<IdentityKey>(initialIdentityKey);
  const [basketIds, setBasketIds] = useState<string[]>([]);

  const setRatio = useCallback((c: keyof RatioTable, v: number) => {
    setRatioState((r) => ({ ...r, [c]: v }));
  }, []);
  const resetRatio = useCallback(() => setRatioState({ ...DEFAULT_RATIO }), []);
  const resetAll = useCallback(() => {
    setRatioState({ ...DEFAULT_RATIO });
    setPrice(DEFAULT_PRICE_PER_FUE);
    setIdentityKey(initialIdentityKey);
    setBasketIds([]);
  }, [initialIdentityKey]);
  const ratioIsDefault = useMemo(
    () => (Object.keys(DEFAULT_RATIO) as (keyof RatioTable)[]).every((k) => ratio[k] === DEFAULT_RATIO[k]),
    [ratio],
  );

  const toggleLever = useCallback((id: string) => {
    setBasketIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  }, []);
  const clearBasket = useCallback(() => setBasketIds([]), []);
  const inBasket = useCallback((id: string) => basketIds.includes(id), [basketIds]);

  // Identität treibt die FUE-Zahl mit (I12): über-gesplittete Schlüssel zählen
  // Personen doppelt ⇒ höherer Bedarf. Personalnummer = konsolidiert.
  const identity = useMemo(() => resolveIdentity(ds.accounts, ds.persons, identityKey, ratio), [ds, identityKey, ratio]);
  const consolidatedFue = useMemo(() => resolveIdentity(ds.accounts, ds.persons, "personalnummer", ratio).fueTotal, [ds, ratio]);
  const { total: contracted } = useContract(ratio);

  const computedKpis = useMemo<Kpis>(() => {
    const t = totals(ds.persons, ratio);
    const ist = Math.round(identity.fueTotal); // schlüsselabhängiger Bedarf
    const entitlement = Math.round(contracted);
    return {
      ...t,
      ist,
      entitlement,
      delta: ist - entitlement,
      deltaEuro: (ist - entitlement) * price,
      afterSafe: ist - t.green,
      restLuecke: ist - t.green - entitlement,
      price,
    };
  }, [ds, ratio, price, identity, contracted]);

  const basketResult = useMemo(() => basket(basketIds, ds.persons, ratio, price), [basketIds, ds, ratio, price]);

  const value: AppState = {
    ds, ratio, setRatio, resetRatio, resetAll, ratioIsDefault, price, setPrice,
    identityKey, setIdentityKey, basketIds, toggleLever, clearBasket, inBasket,
    kpis: computedKpis, identity, consolidatedFue, contracted, basketResult,
    dataSource: ds.meta.source === "seed" ? "seed" : "generator",
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useApp(): AppState {
  const c = useContext(Ctx);
  if (!c) throw new Error("useApp muss innerhalb <AppProvider> stehen");
  return c;
}
